import {
  AIM,
  BALL,
  FIELD,
  IMPACT,
  modeSpeed,
  PLAYER,
  SKILL,
  STAMINA,
  type Ball,
  type InputFrame,
  type Player,
  type Vec2,
} from "@golazo/shared";
import { clamp, clampLength, copyInto, length, len2 } from "../core/math.js";
// Determinista entre motores: ver `core/detMath.ts`.
import { datan2, dcos, dexp, dsin } from "../core/detMath.js";

export interface StepResult {
  dashed: boolean;
  tackled: boolean;
}

/**
 * Simula un jugador durante un tick de duracion fija.
 *
 * El tope de velocidad limita el *pilotaje*, no la velocidad absoluta: se
 * recorta contra el maximo entre MAX_SPEED y la velocidad con la que se
 * entraba al tick. Asi el impulso del dash puede superar el tope y solo el
 * rozamiento lo baja. Recortar siempre a MAX_SPEED anulaba el dash al tick
 * siguiente, y el boton no hacia nada si ya ibas lanzado.
 */
export function stepPlayer(
  player: Player,
  input: InputFrame | null,
  dt: number,
  speedScale = 1,
  tackleBall: { x: number; y: number } | null = null,
  hasBall = false,
): StepResult {
  copyInto(player.prevPos, player.pos);

  if (player.dashCooldown > 0) {
    player.dashCooldown = Math.max(0, player.dashCooldown - dt);
  }
  if (player.tackleCooldown > 0) {
    player.tackleCooldown = Math.max(0, player.tackleCooldown - dt);
  }
  if (player.trapTime > 0) {
    player.trapTime = Math.max(0, player.trapTime - dt);
  }
  if (player.burstTime > 0) {
    player.burstTime = Math.max(0, player.burstTime - dt);
  }
  if (player.hesitateTime > 0) {
    player.hesitateTime = Math.max(0, player.hesitateTime - dt);
  }
  if (player.fakeCooldown > 0) {
    player.fakeCooldown = Math.max(0, player.fakeCooldown - dt);
  }
  if (player.skillCooldown > 0) {
    player.skillCooldown = Math.max(0, player.skillCooldown - dt);
  }
  if (player.diveTime > 0) {
    player.diveTime = Math.max(0, player.diveTime - dt);
  }
  if (player.jumpTime > 0) {
    player.jumpTime = Math.max(0, player.jumpTime - dt);
  }
  if (player.jumpCooldown > 0) {
    player.jumpCooldown = Math.max(0, player.jumpCooldown - dt);
  }
  if (player.diveCooldown > 0) {
    player.diveCooldown = Math.max(0, player.diveCooldown - dt);
  }
  if (player.releaseGrace > 0) {
    player.releaseGrace = Math.max(0, player.releaseGrace - dt);
  }
  if (player.stumbleTime > 0) {
    player.stumbleTime = Math.max(0, player.stumbleTime - dt);
  }
  if (player.braceTime > 0) {
    player.braceTime = Math.max(0, player.braceTime - dt);
  }


  // En el aire no se pilota. Sin esto el portero seguiria "corriendo" hacia el
  // punto que le pide la IA mientras vuela, la estirada perderia direccion y
  // equivocarse de lado dejaria de costar nada.
  const diving = player.diveTime > 0;

  const hasDirection =
    !diving && input !== null && (input.move.x !== 0 || input.move.y !== 0);
  if (hasDirection && input) {
    player.steerIntent.x = input.move.x;
    player.steerIntent.y = input.move.y;
  } else {
    player.steerIntent.x = 0;
    player.steerIntent.y = 0;
  }
  const speed = length(player.vel);

  // Fatiga: esprintar (por encima de DRAIN_THRESHOLD del tope SIN fatigar)
  // gasta resistencia; cualquier otra cosa la recupera. Se mide contra el
  // tope nominal, no el ya fatigado, para que el umbral no se mueva solo.
  const nominalMaxSpeed = modeSpeed(PLAYER.MAX_SPEED) * player.speedMultiplier;
  if (speed > nominalMaxSpeed * STAMINA.DRAIN_THRESHOLD) {
    // Carrera: con mas fondo te cansas menos (2 - 1,04 = 0,96 del gasto).
    const drain = player.talent ? STAMINA.DRAIN_RATE * (2 - player.talent.stamina) : STAMINA.DRAIN_RATE;
    player.stamina = Math.max(0, player.stamina - drain * dt);
  } else {
    player.stamina = Math.min(1, player.stamina + STAMINA.RECOVER_RATE * dt);
  }
  const staminaScale = STAMINA.MIN_SPEED_SCALE + (1 - STAMINA.MIN_SPEED_SCALE) * player.stamina;
  const maxSpeed = nominalMaxSpeed * staminaScale;

  const stumbling = player.stumbleTime > 0;
  const turnScale =
    (hasBall
      ? PLAYER.TURN_WITH_BALL *
        (PLAYER.TURN_AT_SPEED + (1 - PLAYER.TURN_AT_SPEED) * (1 - Math.min(speed / maxSpeed, 1)))
      : 1) * (stumbling ? IMPACT.STUMBLE_ACCEL_SCALE : 1);
  const hesitating = player.hesitateTime > 0;
  // El clamp de mas abajo deja vivir la sobrevelocidad ya alcanzada (para no
  // anular el dash): `Math.max(cap, velocidad_actual)` significa que un tope
  // mas bajo NUNCA gana contra la velocidad de crucero natural de
  // ACCEL/FRICTION una vez alcanzada. Por eso la fatiga (y el desequilibrio)
  // tienen que frenar la propia ACELERACION, no solo el tope: asi el
  // equilibrio natural se desplaza de verdad hacia abajo, en vez de calcular
  // un tope que el clamp jamas llega a aplicar.
  const accelScale =
    (hesitating ? SKILL.HESITATE_ACCEL : 1) *
    (stumbling ? IMPACT.STUMBLE_ACCEL_SCALE : 1) *
    staminaScale;

  // Mientras cargas K, WASD gira la flecha del golpeo: ese ES el apuntado,
  // ya no hay tecla dedicada.
  const steeringShot = player.chargeSteer;

  let dashed = false;
  let tackled = false;

  if (
    !diving &&
    input?.pressed.has("dash") === true &&
    /*
     * El dash vale CON y SIN balon.
     *
     * Estuvo restringido al que lleva el balon porque sin balon hacia lo mismo
     * que la entrada: fijar velocidad hacia una direccion para ir a por el
     * balon. El problema es que ese razonamiento dejaba al que defiende sin
     * ninguna forma de ganar metros, y quien ataca salia siempre con ventaja:
     * el atacante tenia cambio de ritmo y el defensor solo su velocidad
     * de crucero, o sea que un desmarque bien hecho no se podia perseguir.
     *
     * Lo que evita que vuelva a ser "otra entrada" no es prohibirlo, es el
     * ORDEN: `applyBodyCheck` corre ANTES en el tick y, si hay un rival en el
     * cono, se queda el enfriamiento. Con rival cerca, la O es cuerpo; sin
     * nadie a quien embestir, es carrera. Un boton, dos lecturas por
     * situacion, nunca las dos a la vez.
     */
    player.dashCooldown <= 0 &&
    !steeringShot &&
    !hesitating
  ) {
    const dirX = hasDirection ? input.move.x : player.facing.x;
    const dirY = hasDirection ? input.move.y : player.facing.y;
    const dashSpeed = modeSpeed(SKILL.BURST_SPEED);
    player.vel.x = dirX * dashSpeed;
    player.vel.y = dirY * dashSpeed;
    player.dashCooldown = PLAYER.DASH_COOLDOWN;
    if (hasBall) player.burstTime = Math.max(player.burstTime, SKILL.PACE_BURST);
    turnToward(player, { x: dirX, y: dirY }, dt, turnScale);
    player.aimDir.x = player.facing.x;
    player.aimDir.y = player.facing.y;
    dashed = true;
  } else if (
    !diving &&
    tackleBall !== null &&
    player.tackleCooldown <= 0 &&
    !steeringShot
  ) {
    const dx = tackleBall.x - player.pos.x;
    const dy = tackleBall.y - player.pos.y;
    const dist = len2(dx, dy);
    if (dist <= PLAYER.TACKLE_RANGE && dist > 1e-6) {
      const dirX = dx / dist;
      const dirY = dy / dist;
      player.vel.x = dirX * modeSpeed(PLAYER.TACKLE_SPEED);
      player.vel.y = dirY * modeSpeed(PLAYER.TACKLE_SPEED);
      player.tackleCooldown = PLAYER.TACKLE_COOLDOWN;
      turnToward(player, { x: dirX, y: dirY }, dt, turnScale);
      player.aimDir.x = player.facing.x;
      player.aimDir.y = player.facing.y;
      tackled = true;
    }
  } else if (steeringShot) {
    // Apuntando o cargando golpeo: WASD coloca la mira/flecha. El jugador
    // conserva inercia pero no acelera.
    if (hasDirection) {
      const rate = rotateAim(
        player,
        input.move,
        dt,
        player.chargeSteer ? AIM.CHARGE_TURN_RATE : AIM.TURN_RATE,
      );
      /*
       * Se ACUMULA lo barrido, y no decae.
       *
       * La primera version media la velocidad de giro suavizada, y eso hacia
       * la mecanica injugable: en cuanto soltabas el stick para afinar la
       * punteria la comba se desvanecia en una decima de segundo, o sea que
       * cargabas la rosca y la perdias justo antes de disparar. Asi se queda
       * puesta mientras mantengas el boton, y barrer de vuelta la deshace.
       */
      player.aimSweep = clamp(player.aimSweep + rate * dt, -SWEEP_CAP, SWEEP_CAP);
    }
  } else if (hasDirection) {
    // modeSpeed() aqui es obligatorio, no cosmetico: MAX_SPEED/DASH_SPEED/
    // TACKLE_SPEED ya se escalan por modo, pero ACCEL no se escalaba. El
    // clamp de mas abajo deja vivir la sobrevelocidad ya alcanzada (para no
    // anular el dash), asi que sin escalar ACCEL aqui la velocidad de crucero
    // bajo sprint sostenido convergia siempre a ACCEL/FRICTION SIN escalar
    // (~442 u/s) en TODOS los modos, ignorando por completo el ritmo mas
    // lento que 5v5/11v11 estaban pidiendo. Medido: crucero real 424-441 u/s
    // en los cinco modos frente a un tope oficial de 220-389.
    const accel =
      modeSpeed(PLAYER.ACCEL) * accelScale * player.speedMultiplier * (player.burstTime > 0 ? 1.35 : 1);
    player.vel.x += input.move.x * accel * dt;
    player.vel.y += input.move.y * accel * dt;
    turnToward(player, input.move, dt, turnScale);
    // Sin apuntar, la mira acompana a la mirada: golpear "a lo bruto" sigue
    // saliendo hacia donde vas.
    player.aimDir.x = player.facing.x;
    player.aimDir.y = player.facing.y;
  }

  // Velocidad tras input (dash incluido), antes del rozamiento: es la referencia
  // para permitir sobrevelocidad temporal.
  const pilotSpeed = length(player.vel);

  // Fuera de la carga no hay barrido que guardar: un gesto de hace tres
  // segundos no puede rosquear el proximo disparo.
  if (!steeringShot) player.aimSweep = 0;

  // Rozamiento exponencial: independiente del framerate y sin oscilar.
  const friction = steeringShot ? PLAYER.FRICTION * AIM.FRICTION_SCALE : PLAYER.FRICTION;
  const damping = dexp(-friction * dt);
  player.vel.x *= damping;
  player.vel.y *= damping;

  // El tope limita el pilotaje, no la velocidad absoluta. El segundo termino
  // deja vivir una sobrevelocidad ya existente (un dash, o venir lanzado al
  // cerrar el control) pero solo mientras el rozamiento la va comiendo: nunca
  // permite mantenerla a base de acelerar.
  const burstCap = player.burstTime > 0 ? modeSpeed(SKILL.BURST_SPEED) * 0.92 : maxSpeed;
  clampLength(player.vel, Math.max(burstCap * speedScale, pilotSpeed * damping));

  player.pos.x += player.vel.x * dt;
  player.pos.y += player.vel.y * dt;

  confineToField(player);
  return { dashed, tackled };
}

/**
 * Gira la mirada hacia `desired` como mucho TURN_RATE radianes por segundo.
 * El movimiento sigue siendo instantaneo: solo la orientacion tiene inercia.
 */
function turnToward(player: Player, desired: Vec2, dt: number, turnScale = 1): void {
  const target = datan2(desired.y, desired.x);
  const current = datan2(player.facing.y, player.facing.x);

  // Diferencia mas corta, normalizada a [-PI, PI].
  let diff = target - current;
  diff -= Math.PI * 2 * Math.floor((diff + Math.PI) / (Math.PI * 2));

  const maxStep = PLAYER.TURN_RATE * turnScale * dt;
  const next = current + clamp(diff, -maxStep, maxStep);

  player.facing.x = dcos(next);
  player.facing.y = dsin(next);
}

/**
 * Gira la mira hacia `desired`, mas despacio que la mirada. Es un giro propio
 * y no el de `facing` porque hay que poder apuntar a un sitio mientras el
 * cuerpo (y el balon conducido) siguen orientados a otro.
 */
function rotateAim(
  player: Player,
  desired: Vec2,
  dt: number,
  turnRate: number = AIM.TURN_RATE,
): number {
  const target = datan2(desired.y, desired.x);
  const current = datan2(player.aimDir.y, player.aimDir.x);

  let diff = target - current;
  diff -= Math.PI * 2 * Math.floor((diff + Math.PI) / (Math.PI * 2));

  const maxStep = turnRate * dt;
  const step = clamp(diff, -maxStep, maxStep);
  const next = current + step;

  player.aimDir.x = dcos(next);
  player.aimDir.y = dsin(next);
  return dt > 0 ? step / dt : 0;
}

/**
 * Tope del barrido acumulado, en radianes.
 *
 * Sin tope, mantener el boton y dar vueltas con el stick acumularia comba sin
 * fin. Con `SWEEP_FULL` en 0,85 rad, dejar llegar hasta 1,6 da margen para
 * pasarse y volver sin que la mira se quede pegada al tope.
 */
const SWEEP_CAP = 1.6;

/**
 * Integra el balon. La velocidad ya viene fijada por la conduccion (si alguien
 * lo lleva) o por el ultimo golpe/rebote; aqui solo rueda, comba y se frena.
 */
/**
 * Bote en el cesped, con el efecto metido en la ecuacion.
 *
 * Antes el bote solo invertia la velocidad vertical: un balon liftado y uno
 * cortado botaban exactamente igual, que es lo que hace que un juego de futbol
 * se sienta a plastico. En la realidad el bote es donde el efecto se cobra:
 *  - **Liftado**: agarra y sale disparado hacia delante, con bote mas plano.
 *  - **Cortado**: muerde, frena en seco (o vuelve hacia atras) y bota mas alto.
 *  - **Guiñada**: desvia lateralmente al agarrar.
 *
 * El cesped se come casi todo el efecto en el impacto (`BOUNCE_SPIN_KEEP`), asi
 * que el segundo bote ya es mucho mas manso: no hay movimiento perpetuo.
 */
function bounce(ball: Ball): void {
  const speed = len2(ball.vel.x, ball.vel.y);

  // Cortado (topSpin < 0) sube la restitucion; liftado la baja.
  const restitution = Math.max(
    0.05,
    BALL.LOFT_BOUNCE * (1 - BALL.BOUNCE_SPIN_LIFT * ball.topSpin),
  );
  ball.loft = 0;
  ball.loftVel = -ball.loftVel * restitution;

  if (speed > 1e-3) {
    const ux = ball.vel.x / speed;
    const uy = ball.vel.y / speed;

    // Agarre longitudinal: adelante si va liftado, freno si va cortado.
    const grip = BALL.BOUNCE_GRIP * ball.topSpin * speed;
    ball.vel.x += ux * grip;
    ball.vel.y += uy * grip;

    // La guiñada desvia al agarrar, igual que un bote con rosca.
    const side = BALL.BOUNCE_SIDE_KICK * ball.spin * speed;
    ball.vel.x += -uy * side;
    ball.vel.y += ux * side;

    clampLength(ball.vel, BALL.MAX_SPEED);
  }

  ball.topSpin *= BALL.BOUNCE_SPIN_KEEP;
  ball.spin *= BALL.BOUNCE_SPIN_KEEP;
}

export function stepBall(ball: Ball, dt: number): void {
  copyInto(ball.prevPos, ball.pos);
  ball.prevLoft = ball.loft;

  // Efecto: gira el vector velocidad sin cambiar su modulo. Curva la
  // trayectoria sin regalar energia, y se apaga antes de que el balon pare.
  // El efecto se agota SIEMPRE, tambien rodando: si solo decayera en vuelo, un
  // balon que se queda en el suelo conservaria su efecto vertical indefinidamente
  // y reapareceria intacto la proxima vez que alguien lo levantara.
  if (ball.topSpin !== 0) {
    ball.topSpin *= dexp(-BALL.TOPSPIN_DECAY * dt);
    if (Math.abs(ball.topSpin) < 1e-3) ball.topSpin = 0;
  }

  if (ball.spin !== 0) {
    const angle = ball.spin * dt;
    const cos = dcos(angle);
    const sin = dsin(angle);
    const vx = ball.vel.x;
    ball.vel.x = vx * cos - ball.vel.y * sin;
    ball.vel.y = vx * sin + ball.vel.y * cos;
    ball.spin *= dexp(-BALL.SPIN_DECAY * dt);
  }

  if (ball.loft > 0 || ball.loftVel !== 0) {
    ball.loftVel -= BALL.LOFT_GRAVITY * dt;

    // Magnus vertical: el efecto liftado empuja el balon hacia abajo y el
    // cortado lo sostiene. Es proporcional a (efecto x velocidad), asi que un
    // balon lento apenas se dobla y un misil liftado se hunde.
    if (ball.topSpin !== 0) {
      const speed = len2(ball.vel.x, ball.vel.y);
      ball.loftVel -= BALL.MAGNUS_VERT * ball.topSpin * speed * dt * BALL.LOFT_GRAVITY;
    }

    ball.loft += ball.loftVel * dt;
    if (ball.loft <= 0) {
      // Bote en cesped: remates picados y centros siguen vivos un instante.
      if (ball.loftVel < -BALL.LOFT_BOUNCE_MIN) {
        bounce(ball);
      } else {
        ball.loft = 0;
        ball.loftVel = 0;
      }
    }
  }

  // Cesped: frenado viscoso + resistencia a rodadura. Aire: drag cuadratico
  // solo en vuelo.
  let damping = dexp(-BALL.FRICTION * dt);
  const airborne = ball.loft > BALL.LOFT_GROUND_CAPTURE;
  if (airborne) {
    const speed = len2(ball.vel.x, ball.vel.y);
    damping *= 1 / (1 + BALL.AIR_DRAG * speed * dt);
  }
  ball.vel.x *= damping;
  ball.vel.y *= damping;

  // La rodadura solo actua con el balon en el suelo: en el aire no hay cesped
  // que rozar, y aplicarla en vuelo frenaria los centros de forma antinatural.
  if (!airborne) {
    const speed = len2(ball.vel.x, ball.vel.y);
    if (speed > 0) {
      const drop = BALL.ROLL_DECEL * dt;
      if (speed <= drop + BALL.STOP_SPEED) {
        // Para de verdad. El modelo exponencial puro nunca llegaba aqui.
        ball.vel.x = 0;
        ball.vel.y = 0;
        ball.spin = 0;
        ball.topSpin = 0;
      } else {
        const scale = (speed - drop) / speed;
        ball.vel.x *= scale;
        ball.vel.y *= scale;
      }
    }
  }

  ball.pos.x += ball.vel.x * dt;
  ball.pos.y += ball.vel.y * dt;
}

/**
 * Paredes duras: el jugador desliza a lo largo del muro en vez de rebotar.
 *
 * Exportada porque la colocacion del saque de centro tambien recoloca gente a
 * mano y tiene que respetar las mismas paredes: sin esto, empujar a un rival
 * fuera del circulo central podia dejarlo con medio cuerpo fuera del campo.
 */
export function confineToField(player: Player): void {
  const r = player.radius;

  if (player.pos.x < r) {
    player.pos.x = r;
    player.vel.x = 0;
  } else if (player.pos.x > FIELD.WIDTH - r) {
    player.pos.x = FIELD.WIDTH - r;
    player.vel.x = 0;
  }

  if (player.pos.y < r) {
    player.pos.y = r;
    player.vel.y = 0;
  } else if (player.pos.y > FIELD.HEIGHT - r) {
    player.pos.y = FIELD.HEIGHT - r;
    player.vel.y = 0;
  }
}
